import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Scale, Save, Settings, AlertTriangle, Package, Plus, X, RotateCcw, ChevronDown, Boxes, Ruler } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Default weight loss alert causes
const DEFAULT_ALERT_CAUSES = [
  { id: 'robbing', name: 'Robbing', description: 'Bees from other hives stealing honey', selected: true },
  { id: 'starvation', name: 'Starvation', description: 'Colony consuming stores due to lack of forage', selected: true },
  { id: 'swarm', name: 'Swarm Preparation', description: 'Colony preparing to swarm', selected: true },
  { id: 'disease', name: 'Disease/Pests', description: 'Colony weakened by disease or pest infestation', selected: true },
  { id: 'absconding', name: 'Absconding', description: 'Colony abandoning the hive', selected: true },
  { id: 'seasonal', name: 'Seasonal Decline', description: 'Normal weight loss during dearth or winter', selected: true },
];

// Pre-populated weights based on typical beekeeping equipment (in kg)
const DEFAULT_HIVE_TYPE_WEIGHTS = {
  'Langstroth': {
    description: 'Most common hive type worldwide',
    boxes: {
      'Deep Brood (10F)': { weight: 4.5, frames: 10, dimensions: '9-5/8" (244mm) deep' },
      'Deep Brood (8F)': { weight: 3.8, frames: 8, dimensions: '9-5/8" (244mm) deep, 8-frame' },
      'Medium Super (10F)': { weight: 3.2, frames: 10, dimensions: '6-5/8" (168mm) medium' },
      'Medium Super (8F)': { weight: 2.7, frames: 8, dimensions: '6-5/8" (168mm) medium, 8-frame' },
      'Shallow Super (10F)': { weight: 2.5, frames: 10, dimensions: '5-11/16" (145mm) shallow' },
      'Shallow Super (8F)': { weight: 2.1, frames: 8, dimensions: '5-11/16" (145mm) shallow, 8-frame' },
    },
    components: {
      'Screened Bottom Board': 2.5,
      'Solid Bottom Board': 2.0,
      'Slatted Rack': 1.5,
      'Inner Cover': 1.0,
      'Telescopic Lid': 3.5,
      'Migratory Lid': 2.5,
      'Queen Excluder': 0.5,
      'Hive Stand': 3.0,
      'Entrance Reducer': 0.1,
      'Top Feeder': 1.5,
      'Frame Feeder': 0.8,
      'Beetle Trap': 0.3,
      'Mouse Guard': 0.2,
    }
  },
  'Top Bar': {
    description: 'Horizontal hive with top bars, no frames',
    boxes: {
      'Standard Body': { weight: 8.0, frames: 0, dimensions: '48" (1220mm) length typical' },
    },
    components: {
      'Roof/Cover': 3.0,
      'Stand': 4.0,
      'Follower Board': 0.3,
    }
  },
  'Horizontal Hive': {
    description: 'Long horizontal hive design',
    boxes: {
      'Long Lang Body': { weight: 10.0, frames: 20, dimensions: '36-48" (914-1220mm) length' },
      'Layens Style Body': { weight: 12.0, frames: 20, dimensions: '20 deep frames' },
    },
    components: {
      'Hinged Roof': 5.0,
      'Divider Board': 0.5,
      'Stand/Legs': 4.0,
    }
  },
  'Warre': {
    description: 'Vertical top bar hive, minimal intervention',
    boxes: {
      'Warre Box': { weight: 3.0, frames: 8, dimensions: '12" x 12" (305mm) square' },
    },
    components: {
      'Quilt Box': 1.5,
      'Roof': 3.0,
      'Floor': 2.0,
    }
  },
  'Flow Hive': {
    description: 'Langstroth-compatible with Flow frames',
    boxes: {
      'Deep Brood Box': { weight: 4.5, frames: 10, dimensions: '9-5/8" (244mm) deep' },
      'Flow Super (6F)': { weight: 12.0, frames: 6, dimensions: 'Contains Flow frames' },
      'Flow Super (7F)': { weight: 14.0, frames: 7, dimensions: 'Flow Hive 2+ model' },
    },
    components: {
      'Screened Bottom Board': 2.5,
      'Solid Bottom Board': 2.0,
      'Inner Cover': 1.0,
      'Flow Roof': 4.0,
      'Queen Excluder': 0.5,
      'Hive Stand': 3.0,
    }
  },
  'Layens': {
    description: 'Horizontal hive popular in Europe',
    boxes: {
      'Layens Body (14F)': { weight: 12.0, frames: 14, dimensions: '14 deep frames' },
      'Layens Body (20F)': { weight: 15.0, frames: 20, dimensions: '20 deep frames' },
    },
    components: {
      'Divider Board': 0.5,
      'Cover': 5.0,
      'Stand': 4.0,
    }
  },
  'National (UK)': {
    description: 'Common in United Kingdom',
    boxes: {
      'Deep Brood Box': { weight: 4.0, frames: 11, dimensions: '18.125" x 18.125" (460mm)' },
      'Shallow Super': { weight: 2.5, frames: 11, dimensions: '5.5" (140mm) depth' },
    },
    components: {
      'Screened Bottom Board': 2.5,
      'Solid Bottom Board': 2.0,
      'Crown Board': 1.0,
      'Roof': 3.5,
      'Queen Excluder': 0.4,
      'Hive Stand': 3.0,
    }
  },
  'Commercial (UK)': {
    description: 'Larger UK hive for commercial use',
    boxes: {
      'Deep Brood Box': { weight: 5.0, frames: 11, dimensions: '18" x 18" (457mm)' },
      'Shallow Super': { weight: 3.0, frames: 11, dimensions: '5.5" (140mm) depth' },
    },
    components: {
      'Screened Bottom Board': 3.0,
      'Solid Bottom Board': 2.5,
      'Crown Board': 1.2,
      'Roof': 4.0,
      'Queen Excluder': 0.5,
      'Hive Stand': 3.5,
    }
  },
  'Dadant': {
    description: 'Large brood chamber, popular in Europe/Americas',
    boxes: {
      'Dadant Brood Box': { weight: 5.5, frames: 10, dimensions: '11-1/4" (286mm) deep' },
      'Dadant Medium Super': { weight: 3.5, frames: 10, dimensions: '6-1/4" (159mm) depth' },
    },
    components: {
      'Screened Bottom Board': 2.5,
      'Solid Bottom Board': 2.0,
      'Inner Cover': 1.2,
      'Telescopic Lid': 4.0,
      'Queen Excluder': 0.5,
      'Hive Stand': 3.0,
    }
  },
  'Nuc Box': {
    description: 'Nucleus colony box',
    boxes: {
      '5-Frame Nuc': { weight: 2.5, frames: 5, dimensions: '5 frame capacity' },
      '4-Frame Nuc': { weight: 2.0, frames: 4, dimensions: '4 frame capacity' },
      '3-Frame Nuc': { weight: 1.8, frames: 3, dimensions: 'Mini nuc / mating nuc' },
    },
    components: {
      'Nuc Lid': 1.0,
      'Nuc Bottom Board': 1.0,
      'Frame Feeder': 0.3,
    }
  },
};

// Frame weights by type
const DEFAULT_FRAME_WEIGHTS = {
  'Wooden Frame (Deep)': 0.35,
  'Wooden Frame (Medium)': 0.25,
  'Wooden Frame (Shallow)': 0.20,
  'Plastic Frame (Deep)': 0.40,
  'Plastic Frame (Medium)': 0.30,
  'Plastic Frame (Shallow)': 0.25,
  'Flow Frame': 2.5,
  'Top Bar': 0.15,
  'Foundationless Frame': 0.30,
};

// Foundation weights
const DEFAULT_FOUNDATION_WEIGHTS = {
  'Wax Foundation (Deep)': 0.12,
  'Wax Foundation (Medium)': 0.08,
  'Wax Foundation (Shallow)': 0.06,
  'Plastic Foundation': 0.15,
  'Waxed Plastic': 0.18,
  'Foundationless (Starter Strip)': 0.02,
  'Drone Comb Foundation': 0.10,
};

const sectionLabels = {
  lids_covers: 'Lids & Covers',
  feeders: 'Feeders',
  queen_bee_management: 'Queen Bee Management',
  bottom_boards_stands: 'Bottom Boards & Stands',
  pest_control: 'Pest Control',
  heavy_accessories: 'Heavy Accessories',
  other: 'Other Components'
};

const WeightConfigurationView = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [selectedHiveType, setSelectedHiveType] = useState('Langstroth');
  const [unitPreference, setUnitPreference] = useState('kg');
  const [dimensionUnit, setDimensionUnit] = useState('both');
  const [alertThreshold, setAlertThreshold] = useState(10);
  const [allHiveTypesFromAPI, setAllHiveTypesFromAPI] = useState([]);
  const [alertCauses, setAlertCauses] = useState(DEFAULT_ALERT_CAUSES);
  const [adminComponents, setAdminComponents] = useState([]);
  const [componentWeights, setComponentWeights] = useState({});
  const [newCauseName, setNewCauseName] = useState('');
  const [newCauseDescription, setNewCauseDescription] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    general: new URLSearchParams(window.location.search).get('section') === 'general',
    alertCauses: false,
    adminComponents: false,
    hiveType: false,
    frames: false,
    foundation: false
  });

  const token = localStorage.getItem('token');
  
  // Ref to store scroll restoration data
  const scrollRestoreRef = useRef(null);

  // Find the nearest scrollable parent element
  const toggleSection = useCallback((section, cardElement) => {
    if (cardElement) {
      // Find the actual scrollable parent (the Dashboard content div has overflow-y-auto)
      let scrollParent = cardElement.parentElement;
      while (scrollParent) {
        const style = window.getComputedStyle(scrollParent);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll') break;
        scrollParent = scrollParent.parentElement;
      }
      scrollRestoreRef.current = {
        element: cardElement,
        viewportTop: cardElement.getBoundingClientRect().top,
        scrollParent: scrollParent || null,
      };
    }
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);
  
  // Restore scroll position synchronously after DOM update to prevent visible jump
  useLayoutEffect(() => {
    if (scrollRestoreRef.current) {
      const { element, viewportTop, scrollParent } = scrollRestoreRef.current;
      scrollRestoreRef.current = null;
      
      const newTop = element.getBoundingClientRect().top;
      const drift = newTop - viewportTop;
      if (Math.abs(drift) > 1) {
        if (scrollParent) {
          scrollParent.scrollTop += drift;
        } else {
          window.scrollBy(0, drift);
        }
      }
    }
  }, [expandedSections]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [configRes, hiveTypesRes, componentsRes] = await Promise.all([
        axios.get(`${API}/api/user/weight-settings`, { headers }).catch(() => null),
        axios.get(`${API}/api/config/hive_types`, { headers }).catch(() => null),
        axios.get(`${API}/api/admin/config/components`, { headers }).catch(() => null)
      ]);
      
      if (componentsRes?.data?.items) {
        setAdminComponents(componentsRes.data.items.filter(c => c.is_active !== false));
      }
      
      let apiHiveTypes = [];
      if (hiveTypesRes?.data) {
        apiHiveTypes = hiveTypesRes.data.map(ht => ({
          name: ht.name, description: ht.description || 'Hive type', requires_boxes: ht.requires_boxes, isFromAPI: true
        }));
        setAllHiveTypesFromAPI(apiHiveTypes);
      }
      
      const mergedHiveTypeWeights = { ...DEFAULT_HIVE_TYPE_WEIGHTS };
      apiHiveTypes.forEach(ht => {
        if (!mergedHiveTypeWeights[ht.name]) {
          mergedHiveTypeWeights[ht.name] = {
            description: ht.description,
            boxes: { 'Main Body': { weight: 5.0, frames: 10, dimensions: 'Custom' } },
            components: { 'Cover': 2.0, 'Floor': 2.0 }
          };
        }
      });
      
      if (configRes?.data && Object.keys(configRes.data).length > 0) {
        const mergedConfig = {
          unit_preference: configRes.data.unit_preference || 'kg',
          dimension_unit: configRes.data.dimension_unit || 'both',
          alert_threshold_percent: configRes.data.alert_threshold_percent || 10,
          hive_type_weights: { ...mergedHiveTypeWeights, ...(configRes.data.hive_type_weights || {}) },
          frame_weights: { ...DEFAULT_FRAME_WEIGHTS, ...(configRes.data.frame_weights || {}) },
          foundation_weights: { ...DEFAULT_FOUNDATION_WEIGHTS, ...(configRes.data.foundation_weights || {}) },
        };
        setConfig(mergedConfig);
        setUnitPreference(mergedConfig.unit_preference);
        setDimensionUnit(mergedConfig.dimension_unit);
        setAlertThreshold(mergedConfig.alert_threshold_percent);
        if (configRes.data.alert_causes?.length > 0) setAlertCauses(configRes.data.alert_causes);
        if (configRes.data.component_weights) setComponentWeights(configRes.data.component_weights);
      } else {
        setConfig({
          unit_preference: 'kg', dimension_unit: 'both', alert_threshold_percent: 10,
          hive_type_weights: mergedHiveTypeWeights,
          frame_weights: { ...DEFAULT_FRAME_WEIGHTS },
          foundation_weights: { ...DEFAULT_FOUNDATION_WEIGHTS },
        });
      }
    } catch (error) {
      setConfig({
        unit_preference: 'kg', dimension_unit: 'both', alert_threshold_percent: 10,
        hive_type_weights: { ...DEFAULT_HIVE_TYPE_WEIGHTS },
        frame_weights: { ...DEFAULT_FRAME_WEIGHTS },
        foundation_weights: { ...DEFAULT_FOUNDATION_WEIGHTS },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API}/api/user/weight-settings`, {
        ...config, unit_preference: unitPreference, dimension_unit: dimensionUnit,
        alert_threshold_percent: alertThreshold, alert_causes: alertCauses, component_weights: componentWeights,
      }, { headers });
      toast.success('Weight configuration saved successfully');
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = async () => {
    if (!window.confirm('Reset all weight settings to defaults? This cannot be undone.')) return;
    setResetting(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/api/user/weight-settings`, { headers });
      toast.success('Settings reset to defaults');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to reset settings');
    } finally {
      setResetting(false);
    }
  };

  const updateBoxWeight = (hiveType, boxName, weight) => {
    setConfig(prev => {
      const currentHiveData = prev.hive_type_weights?.[hiveType] || {};
      const currentBoxes = currentHiveData.boxes || {};
      const currentBox = currentBoxes[boxName] || {};
      return {
        ...prev,
        hive_type_weights: {
          ...prev.hive_type_weights,
          [hiveType]: { ...currentHiveData, boxes: { ...currentBoxes, [boxName]: { ...currentBox, weight: parseFloat(weight) || 0 } } }
        }
      };
    });
  };

  const updateComponentWeight = (hiveType, componentName, weight) => {
    setConfig(prev => {
      const currentHiveData = prev.hive_type_weights?.[hiveType] || {};
      const currentComponents = currentHiveData.components || {};
      return {
        ...prev,
        hive_type_weights: {
          ...prev.hive_type_weights,
          [hiveType]: { ...currentHiveData, components: { ...currentComponents, [componentName]: parseFloat(weight) || 0 } }
        }
      };
    });
  };

  const updateFrameWeight = (frameName, weight) => {
    setConfig(prev => ({ ...prev, frame_weights: { ...prev.frame_weights, [frameName]: parseFloat(weight) || 0 } }));
  };

  const updateFoundationWeight = (foundationName, weight) => {
    setConfig(prev => ({ ...prev, foundation_weights: { ...prev.foundation_weights, [foundationName]: parseFloat(weight) || 0 } }));
  };

  const toggleAlertCause = (causeId) => {
    setAlertCauses(prev => prev.map(cause => cause.id === causeId ? { ...cause, selected: !cause.selected } : cause));
  };

  const addCustomCause = () => {
    if (!newCauseName.trim()) { toast.error('Please enter a cause name'); return; }
    setAlertCauses(prev => [...prev, { id: `custom_${Date.now()}`, name: newCauseName.trim(), description: newCauseDescription.trim() || 'Custom cause', selected: true, isCustom: true }]);
    setNewCauseName(''); setNewCauseDescription('');
    toast.success('Custom cause added');
  };

  const removeCustomCause = (causeId) => {
    setAlertCauses(prev => prev.filter(cause => cause.id !== causeId));
    toast.success('Custom cause removed');
  };

  const updateAdminComponentWeight = (componentId, weight) => {
    setComponentWeights(prev => ({ ...prev, [componentId]: parseFloat(weight) || 0 }));
  };

  const getComponentWeight = (component) => {
    if (componentWeights[component.id] !== undefined) return componentWeights[component.id];
    return component.default_weight_kg || 0;
  };

  const formatDimensions = (dimensionText) => {
    if (!dimensionText) return null;
    const inchesMatch = dimensionText.match(/^([^(]+)/);
    const mmMatch = dimensionText.match(/\((\d+(?:mm|-\d+mm)?)\)/);
    const suffixMatch = dimensionText.match(/\)\s*(.+)$/);
    const inchesPart = inchesMatch ? inchesMatch[1].trim() : '';
    const mmPart = mmMatch ? mmMatch[1] : '';
    const suffix = suffixMatch ? suffixMatch[1].trim() : '';
    if (dimensionUnit === 'inches') return inchesPart + (suffix ? ' ' + suffix : '');
    if (dimensionUnit === 'mm') return mmPart + (suffix ? ' ' + suffix : '');
    return dimensionText;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-amber-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const allHiveTypes = [...Object.keys(DEFAULT_HIVE_TYPE_WEIGHTS), ...Object.keys(config?.hive_type_weights || {}).filter(t => !DEFAULT_HIVE_TYPE_WEIGHTS[t])];
  const currentHiveTypeData = config?.hive_type_weights?.[selectedHiveType] || DEFAULT_HIVE_TYPE_WEIGHTS[selectedHiveType];
  const frameWeights = config?.frame_weights || DEFAULT_FRAME_WEIGHTS;
  const foundationWeights = config?.foundation_weights || DEFAULT_FOUNDATION_WEIGHTS;

  // Group admin components by section
  const groupedComponents = adminComponents.reduce((acc, comp) => {
    const section = comp.section || 'other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(comp);
    return acc;
  }, {});

  // Collapsible Section Component - using CSS-based expansion to prevent scroll jumps
  const CollapsibleCard = ({ id, icon: Icon, iconColor, title, subtitle, children }) => {
    const cardRef = useRef(null);
    const isExpanded = expandedSections[id];
    
    const handleToggle = useCallback(() => {
      toggleSection(id, cardRef.current);
    }, [id, toggleSection]);
    
    return (
      <div ref={cardRef} className="bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        <div
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); } }}
          className="w-full px-5 py-4 flex items-center justify-between hover:bg-stone-50 transition-colors cursor-pointer select-none"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-xl ${iconColor}`}>
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-stone-800">{title}</h3>
              {subtitle && <p className="text-sm text-stone-500">{subtitle}</p>}
            </div>
          </div>
          <div className={`p-1 rounded-full transition-colors ${isExpanded ? 'bg-amber-100' : 'bg-stone-100'}`}>
            <ChevronDown className={`h-5 w-5 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-amber-600' : 'text-stone-400'}`} />
          </div>
        </div>
        <div
          className="grid"
          style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            <div className="px-5 pb-5 border-t border-stone-100 pt-4">
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Scale className="h-6 w-6 text-amber-600" />
          Weight Configuration
        </h2>
        <div className="flex gap-2 flex-wrap">
            <Button onClick={handleResetToDefaults} disabled={resetting} variant="outline" size="sm">
              <RotateCcw className="h-4 w-4 mr-2" />{resetting ? 'Resetting...' : 'Reset'}
            </Button>
            <Button data-testid="weight-config-save-btn" onClick={handleSave} disabled={saving} size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
              <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
            </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-amber-600">{unitPreference.toUpperCase()}</div>
          <div className="text-xs text-amber-700 mt-1 font-medium">Weight Unit</div>
        </div>
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-orange-600">{alertThreshold}%</div>
          <div className="text-xs text-orange-700 mt-1 font-medium">Alert Threshold</div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">{alertCauses.filter(c => c.selected).length}</div>
          <div className="text-xs text-blue-700 mt-1 font-medium">Active Causes</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-xl p-4 text-center">
          <div className="text-2xl font-bold text-emerald-600">{adminComponents.length}</div>
          <div className="text-xs text-emerald-700 mt-1 font-medium">Components</div>
        </div>
      </div>

      {/* General Settings */}
      <CollapsibleCard id="general" icon={Settings} iconColor="bg-stone-500" title="General Settings" subtitle="Units and alert preferences">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-stone-50 rounded-xl p-4">
            <Label className="text-stone-600 text-sm font-medium">Weight Unit</Label>
            <Select value={unitPreference} onValueChange={setUnitPreference}>
              <SelectTrigger data-testid="weight-unit-select" className="mt-2 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="kg">Kilograms (kg)</SelectItem>
                <SelectItem value="lbs">Pounds (lbs)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-stone-500 mt-2">Your one unit for everything: hive weights, inspections, harvests, analytics and PDFs. Remember to Save.</p>
          </div>
          <div className="bg-stone-50 rounded-xl p-4">
            <Label className="text-stone-600 text-sm font-medium">Dimension Display</Label>
            <Select value={dimensionUnit} onValueChange={setDimensionUnit}>
              <SelectTrigger className="mt-2 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="inches">Inches only</SelectItem>
                <SelectItem value="mm">Millimeters only</SelectItem>
                <SelectItem value="both">Both (in & mm)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="bg-stone-50 rounded-xl p-4">
            <Label className="text-stone-600 text-sm font-medium">Alert Threshold</Label>
            <div className="flex items-center gap-2 mt-2">
              <Input type="number" value={alertThreshold} onChange={(e) => setAlertThreshold(parseInt(e.target.value) || 10)} className="w-20 bg-white" min="1" max="50" />
              <span className="text-sm text-stone-500">% weight loss</span>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* Weight Loss Alert Causes */}
      <CollapsibleCard id="alertCauses" icon={AlertTriangle} iconColor="bg-orange-500" title="Weight Loss Alert Causes" subtitle="Causes shown when weight loss detected">
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {alertCauses.map(cause => (
              <div key={cause.id} onClick={() => toggleAlertCause(cause.id)}
                className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${cause.selected ? 'border-orange-400 bg-orange-50 shadow-sm' : 'border-stone-200 bg-white hover:border-stone-300'}`}>
                <div className="flex items-center justify-between">
                  <span className={`font-medium text-sm ${cause.selected ? 'text-orange-700' : 'text-stone-700'}`}>{cause.name}</span>
                  <div className="flex items-center gap-2">
                    {cause.isCustom && <button onClick={(e) => { e.stopPropagation(); removeCustomCause(cause.id); }} className="text-stone-400 hover:text-red-500"><X className="h-4 w-4" /></button>}
                    <Switch checked={cause.selected} onCheckedChange={() => toggleAlertCause(cause.id)} />
                  </div>
                </div>
                <p className="text-xs text-stone-500 mt-1 line-clamp-2">{cause.description}</p>
              </div>
            ))}
          </div>
          <div className="bg-stone-50 rounded-xl p-4">
            <Label className="text-sm font-medium text-stone-700">Add Custom Cause</Label>
            <div className="flex flex-col sm:flex-row gap-2 mt-2">
              <Input placeholder="Cause name" value={newCauseName} onChange={(e) => setNewCauseName(e.target.value)} className="flex-1 bg-white" />
              <Input placeholder="Description (optional)" value={newCauseDescription} onChange={(e) => setNewCauseDescription(e.target.value)} className="flex-1 bg-white" />
              <Button onClick={addCustomCause} className="bg-orange-500 hover:bg-orange-600"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
          </div>
        </div>
      </CollapsibleCard>

      {/* Hive Component Weights - Grouped by Section */}
      {adminComponents.length > 0 && (
        <CollapsibleCard id="adminComponents" icon={Package} iconColor="bg-blue-500" title="Hive Component Weights" subtitle="Weights for baseline calculations">
          <div className="space-y-4">
            {Object.entries(groupedComponents).map(([section, components]) => (
              <div key={section} className="bg-stone-50 rounded-xl p-4">
                <h4 className="font-medium text-stone-700 mb-3 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                  {sectionLabels[section] || section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </h4>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {components.map(comp => (
                    <div key={comp.id} className="bg-white rounded-lg p-3 border border-stone-200">
                      <Label className="text-xs text-blue-600 font-medium truncate block">{comp.name}</Label>
                      <div className="flex items-center gap-1 mt-2">
                        <Input type="number" step="0.1" value={getComponentWeight(comp)} onChange={(e) => updateAdminComponentWeight(comp.id, e.target.value)} className="h-8 text-sm bg-stone-50" />
                        <span className="text-xs text-stone-400 w-6">{unitPreference}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </CollapsibleCard>
      )}

      {/* Hive Type Baseline Weights */}
      <CollapsibleCard id="hiveType" icon={Boxes} iconColor="bg-amber-500" title="Hive Type Baseline Weights" subtitle="Box and component weights per hive type">
        <div className="space-y-4">
          <div className="bg-amber-50 rounded-xl p-4">
            <Label className="text-sm text-amber-700 font-medium">Select Hive Type</Label>
            <Select value={selectedHiveType} onValueChange={setSelectedHiveType}>
              <SelectTrigger className="mt-2 bg-white border-amber-200"><SelectValue /></SelectTrigger>
              <SelectContent>{allHiveTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent>
            </Select>
            {currentHiveTypeData?.description && <p className="text-xs text-amber-600 mt-2">{currentHiveTypeData.description}</p>}
          </div>

          {currentHiveTypeData?.boxes && Object.keys(currentHiveTypeData.boxes).length > 0 && (
            <div className="bg-stone-50 rounded-xl p-4">
              <h4 className="font-medium text-stone-700 mb-3 flex items-center gap-2"><Boxes className="h-4 w-4 text-amber-600" />Box Weights - {selectedHiveType}</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {Object.entries(currentHiveTypeData.boxes).map(([boxName, data]) => (
                  <div key={boxName} className="bg-white rounded-lg p-3 border border-stone-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium text-stone-700 text-sm">{boxName}</span>
                        {data.frames > 0 && <span className="text-xs text-stone-400 ml-2">({data.frames}F)</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <Input type="number" step="0.1" value={data.weight || 0} onChange={(e) => updateBoxWeight(selectedHiveType, boxName, e.target.value)} className="w-20 h-8 text-sm text-right bg-stone-50" />
                        <span className="text-xs text-stone-400 w-6">{unitPreference}</span>
                      </div>
                    </div>
                    {data.dimensions && <p className="text-xs text-stone-400 mt-1">{formatDimensions(data.dimensions)}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {currentHiveTypeData?.components && Object.keys(currentHiveTypeData.components).length > 0 && (
            <div className="bg-stone-50 rounded-xl p-4">
              <h4 className="font-medium text-stone-700 mb-3 flex items-center gap-2"><Scale className="h-4 w-4 text-amber-600" />Component Weights - {selectedHiveType}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.entries(currentHiveTypeData.components).map(([name, weight]) => (
                  <div key={name} className="bg-white rounded-lg p-3 border border-stone-200">
                    <span className="text-sm text-stone-700 truncate block">{name}</span>
                    <div className="flex items-center gap-1 mt-2">
                      <Input type="number" step="0.1" value={typeof weight === 'number' ? weight : 0} onChange={(e) => updateComponentWeight(selectedHiveType, name, e.target.value)} className="h-8 text-sm bg-stone-50" />
                      <span className="text-xs text-stone-400 w-6">{unitPreference}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsibleCard>

      {/* Frame Weights */}
      <CollapsibleCard id="frames" icon={Ruler} iconColor="bg-emerald-500" title="Frame Weights" subtitle="Empty frame weights (no foundation/comb)">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(frameWeights).map(([name, weight]) => (
            <div key={name} className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
              <span className="text-sm text-emerald-700 truncate block">{name}</span>
              <div className="flex items-center gap-1 mt-2">
                <Input type="number" step="0.01" value={typeof weight === 'number' ? weight : 0} onChange={(e) => updateFrameWeight(name, e.target.value)} className="h-8 text-sm bg-white" />
                <span className="text-xs text-emerald-600 w-6">{unitPreference}</span>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleCard>

      {/* Foundation Weights */}
      <CollapsibleCard id="foundation" icon={Scale} iconColor="bg-purple-500" title="Foundation Weights" subtitle="Foundation weights by type">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {Object.entries(foundationWeights).map(([name, weight]) => (
            <div key={name} className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <span className="text-sm text-purple-700 truncate block">{name}</span>
              <div className="flex items-center gap-1 mt-2">
                <Input type="number" step="0.01" value={typeof weight === 'number' ? weight : 0} onChange={(e) => updateFoundationWeight(name, e.target.value)} className="h-8 text-sm bg-white" />
                <span className="text-xs text-purple-600 w-6">{unitPreference}</span>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleCard>

      {/* Floating Save Button for Mobile */}
      <div className="fixed bottom-6 right-6 sm:hidden">
        <Button onClick={handleSave} disabled={saving} className="bg-amber-500 hover:bg-amber-600 shadow-lg rounded-full h-14 w-14 p-0">
          <Save className="h-6 w-6" />
        </Button>
      </div>
    </div>
  );
};

export default WeightConfigurationView;
